/**
 * DateField — a keyboard-first date text input built on React Aria's accessible
 * date-segment field: typed `MM / DD / YYYY` entry with arrow-key increment,
 * segment-by-segment focus, and screen-reader support, styled to match the kit's
 * text inputs. An optional `trailing` slot sits INSIDE the field box (right-aligned)
 * for a calendar-picker button or similar affordance.
 *
 * It speaks ISO `'YYYY-MM-DD'` strings at its edge (like `@mantine/dates`),
 * converting to/from React Aria's `CalendarDate` internally. Locale is pinned to
 * en-US so the server and client render the SAME segment order — otherwise a
 * locale-detected field can hydrate with a different layout and throw.
 */
import { useMemo, type ReactElement, type ReactNode } from 'react';
import {
  DateField as AriaDateField,
  DateInput,
  DateSegment,
  I18nProvider,
  Label,
} from 'react-aria-components';
import { type CalendarDate, parseDate } from '@internationalized/date';
import styles from './DateField.module.css';

/** Coalesce a (strict `string | undefined`) CSS-module class to a concrete string —
 *  React Aria's `className` props don't accept `undefined` under exactOptionalProps. */
function cx(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** ISO `'YYYY-MM-DD'` string → React Aria `CalendarDate` (null on empty/invalid). */
function toCalendarDate(value: string | null | undefined): CalendarDate | null {
  if (value === null || value === undefined || value === '') return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

export interface DateFieldProps {
  /** ISO `'YYYY-MM-DD'` date, or null when unset (renders placeholder segments). */
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  /** Inclusive ISO bounds — typing/stepping past them is clamped by React Aria. */
  readonly minValue?: string | undefined;
  readonly maxValue?: string | undefined;
  readonly isDisabled?: boolean;
  /** Accessible name when no visible `label` is rendered. */
  readonly 'aria-label'?: string | undefined;
  /** Optional visible label, associated with the field for free. */
  readonly label?: string | undefined;
  /** Rendered inside the field box, right-aligned (e.g. a calendar-picker button). */
  readonly trailing?: ReactNode;
}

export function DateField({
  value,
  onChange,
  minValue,
  maxValue,
  isDisabled = false,
  'aria-label': ariaLabel,
  label,
  trailing,
}: DateFieldProps): ReactElement {
  // Memoize the derived CalendarDate objects on their ISO string. React Aria treats a
  // new `value` object as an external change and RE-DERIVES its segment state from it —
  // so a re-render that recreates an equal-but-new CalendarDate (e.g. the segment count
  // query returning mid-edit) would wipe the user's in-progress typing. Keeping stable
  // object identity while the string is unchanged lets React Aria own the editing.
  const dateValue = useMemo(() => toCalendarDate(value), [value]);
  const min = useMemo(() => toCalendarDate(minValue), [minValue]);
  const max = useMemo(() => toCalendarDate(maxValue), [maxValue]);
  return (
    <I18nProvider locale="en-US">
      <AriaDateField
        className={cx(styles.field)}
        value={dateValue}
        onChange={(next) => onChange(next !== null ? next.toString() : null)}
        isDisabled={isDisabled}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        {...(min !== null ? { minValue: min } : {})}
        {...(max !== null ? { maxValue: max } : {})}
      >
        {label !== undefined ? <Label className={cx(styles.label)}>{label}</Label> : null}
        <div className={cx(styles.box)}>
          <DateInput className={cx(styles.input)}>
            {(segment) => <DateSegment className={cx(styles.segment)} segment={segment} />}
          </DateInput>
          {trailing !== undefined ? <div className={cx(styles.trailing)}>{trailing}</div> : null}
        </div>
      </AriaDateField>
    </I18nProvider>
  );
}
